import { a as useAuthStore, u as useSessionStore, v as logger, b as useConfigStore, h as ppapiClient, n as apiResponse, H as generateUUID } from './prodsel.store-B-MQAyQE.mjs';
import { storeToRefs } from 'pinia';
import 'vue';
import { isFunction } from 'lodash';

const PP_SHOP_LIB_VERSION = "1.0.4";

class Order {

    constructor(cartId, cart, checkout, kid) {

        this.cart = cart;
        this.checkout = {};
        this.error = [];
        this.conf = {
            cartId: cartId,
            customerId: kid,
            destinationCountry: null,
            paymentType: null,
            bankaccount: null,
            invoiceAddress: {},
            invoiceAddressId: false,
            shippingAddress: {},
            shippingAddressId: false,
            orderItems: []
        };

        if (checkout) this.setCheckout(checkout);

        if (cart && checkout) this.init();

    }

    setCheckout(checkout) {
        this.checkout = checkout;
        this.conf.destinationCountry = checkout.destinationCountry;
        this.conf.paymentType = checkout.payment || null;
        this.conf.login = Boolean(checkout?.login);
    }

    setCart(cart) {
        this.cart = cart;
    }

    init() {

        const authStore = useAuthStore();
        const sessionStore = useSessionStore();

        if (authStore.setup?.sessionId) {
            this.conf.sessionId = authStore.setup.sessionId;
        }

        // Benutzer-Identifikation: Entweder customerId (registriert) oder email (Gast)
        if (authStore.user?.kid) {
            this.conf.customerId = authStore.user.kid;
        } else if (sessionStore.checkout?.email) {
            // Für nicht eingeloggte Benutzer die Email verwenden
            this.conf.email = sessionStore.checkout.email;
            // customerId nicht mitgeben für Gastkunden
            delete this.conf.customerId;
        }

        // Warenkorb auslesen
        this.cart.forEach(item => {

            try {
                this.conf.orderItems.push({
                    pid: item.product.pid,
                    quantity: item.quantity,
                    copies: item.copies,
                    revision: item.revision,
                    properties: item.properties,
                    mediaSettings: item.mediaSettings,
                    media: this.removeComponents(item.media), // media components are not neccesary and can slow down api request
                    mediaCount: item.media.length
                });
            } catch (error) {
                logger.error('cart error');
                return false
            }

        });

        // Checkout Parameter auslesen

        try {
            
            let deliveryAdddrType = "R";
            if (this.checkout.altDelivery) deliveryAdddrType = "L";
            
            this.conf.invoiceAddress = sessionStore.addresses.rawData.find((adr) => adr.sid == this.checkout.addresses.R);
            this.conf.shippingAddress = sessionStore.addresses.rawData.find((adr) => adr.sid == this.checkout.addresses[deliveryAdddrType]);

            const selectedBankaccountZid = this.checkout.bankaccount;
            const availableBankaccounts = Array.isArray(sessionStore.bankaccounts?.rawData)
                ? sessionStore.bankaccounts.rawData
                : [];

            if (selectedBankaccountZid) {
                const selectedBankaccount = availableBankaccounts.find((account) => account?.zid == selectedBankaccountZid) || null;
                this.conf.bankaccount = selectedBankaccount
                    ? {
                        ...selectedBankaccount,
                        holderName: selectedBankaccount.holderName || selectedBankaccount.owner || ''
                    }
                    : null;
            } else {
                this.conf.bankaccount = null;
            }
             
        } catch (error) {
            logger.error("checkout error. Failed to set addresses");
            this.error.push("checkout error. Failed to set addresses");
            return false
        }

        return true;
    }

    removeComponents(media) {

        const out = [];      
        media.forEach((m) => {
            let mStripped = {};
            Object.keys(m).forEach((k) => {
                if (k != "components") mStripped[k] = m[k];
            });
            out.push(mStripped);
        });

        return out
        
    }

    getClientInfo() {
        // Get app information from configStore
        // this value will be included in the order payload to identify the client application
        const configStore = useConfigStore();
        return {
            client: configStore.app || null,
            clientVersion: PP_SHOP_LIB_VERSION,
        }
    }

    async validate() {

        const sessionStore = useSessionStore();
        const checkoutId = sessionStore.checkout?.checkoutId || null;

        try {
            var response = await ppapiClient.post("orders/validate", {
                ...this.getClientInfo(),
                ...this.conf,
                ...(checkoutId ? { checkoutId } : {})
            });
        } catch (e) {
            this.error.push(e);
            throw e
        }

        return response

    }

    async send() {

        try {
            var response = await ppapiClient.post("orders", {
                ...this.getClientInfo(),
                ...this.conf
            })
                ;
        } catch (e) {
            this.error.push(e);
            throw e
        }

        return response

    }

    async sendByCartId(extraPayload = {}) {

        const sessionStore = useSessionStore();
        const checkoutId = sessionStore.checkout?.checkoutId || null;

        try {
            var response = await ppapiClient.post("orders", {
                ...this.getClientInfo(),
                cartId: this.conf.cartId,
                ...(this.conf.sessionId ? { sessionId: this.conf.sessionId } : {}),
                ...(checkoutId ? { checkoutId } : {}),
                ...extraPayload
            });
        } catch (e) {
            this.error.push(e);
            throw e
        }

        return response

    }

    toJSON() {

        return JSON.stringify(this.conf)

    }

}

class AddressManager {

  constructor() {

    const sessionStore = useSessionStore();

    this.authStore = useAuthStore();
    this.raddr = {};
    this.laddr = {};
    this.onAddrChange = null;

    const { checkout, addresses } = storeToRefs(sessionStore);

    this.checkout = checkout;
    this.addresses = addresses;


    // Initialize addresses as API Response class if rawData exists, since class methods are not saved in the store
    if (addresses.value?.rawData ) addresses.value = new apiResponse(addresses.value.rawData);
    else this.createAddresses();
  }

  async refreshAddresses() {
    // load addresses throug API call and update addresses, while keeping newly created addresses (sid starts with "_")
    if (!this.authStore.user) {
      let newAddresses = this.filterNewAddresses(this.addresses.value.data()).map((address) => this.normalizeLegacyCountry(address));
        if (newAddresses.length === 0) {
            newAddresses = this.createAddresses().data();
        }
        this.addresses.value = new apiResponse(newAddresses);
        this.addresses.value.loaded = true;
        return Promise.resolve(this.addresses.value);
    } 

    return ppapiClient.get("users/private/addresses/active")
        .then(response => {
            if (response.data && response.data().length > 0) {
                // Filter newly created addresses
                const savedAddresses = response.data().map((address) => this.normalizeLegacyCountry(address));
                let newAddresses = [];
                let serverData = this.addresses.value.data();
                if (Array.isArray(serverData)) newAddresses = this.filterNewAddresses(serverData).map((address) => this.normalizeLegacyCountry(address));
                this.addresses.value = new apiResponse([...newAddresses, ...savedAddresses]);
                this.addresses.value.loaded = true;
            } else {
                this.createAddresses([]);
            }
        })
        .catch(err => {
            logger.error("Error loading addresses from db", err);
            throw err;
        });
}

normalizeLegacyCountry(address) {
  if (!address || String(address.countryISO || '').trim()) return address

  const countryISO = {
    D: 'DE',
    A: 'AT'
  }[String(address.country || '').trim().toUpperCase()];

  if (countryISO) address.countryISO = countryISO;
  return address
}

filterNewAddresses(addresses) {
  // filters local addresses that have at minimum one field set
  return  addresses.filter(addr => 
    addr && String(addr.sid).startsWith("_") && ( addr.type || addr.firstname || addr.lastname || addr.company || addr.street || addr.zip || addr.city || addr.countryISO )
  );
    
}

 async init() {

    let addr_R_sel = false;
    let addr_L_sel = false;

    if (this.checkout.value.addresses) {
        if (this.checkout.value.addresses.R) addr_R_sel = this.checkout.value.addresses["R"];
        if (this.checkout.value.addresses.L) addr_L_sel = this.checkout.value.addresses["L"];
    }
    
    try {
        // Wenn der Nutzer eingeloggt ist, dann werden die Adressen aus dem Store mit den serverdaten überschrieben
        // neu erstellte Adressen werden behalten
        await this.refreshAddresses();
        if (this.checkout.value.selectStandardInvoiceAddressAfterLogin) {
          this.setAddr("R",this.addresses.value.data().find((el) => el.type == "S"));
          this.checkout.value.selectStandardInvoiceAddressAfterLogin = false;
        } else {
          if (addr_R_sel) this.setAddr("R",this.addresses.value.data().find((el) => el.sid == addr_R_sel));
          if (!this.raddr?.sid) this.setAddr("R",this.addresses.value.data().find((el) => el.type == "S"));
        }

        if (addr_L_sel) this.setAddr("L",this.addresses.value.data().find((el) => el.sid == addr_L_sel));
        if (!this.laddr?.sid) this.setAddr("L",this.addresses.value.data().find((el) => el.type == "S"));

    } catch (e) {
        logger.error("Error loading addresses", e);
        throw e;
    }
    

    if (!this.addresses.value || !this.addresses.value.data || typeof this.addresses.value.data()[0] == "undefined") {
        this.createAddresses();
        this.setAddr("R",this.addresses.value.data()[0]);
    }

  }


  createAddresses(addressData) {

    // Fallback, wenn keine Adressen geladen werden können (nutzer nicht eingeloggt)

    if (!addressData) addressData =  [
        this.addrTemplate(),
    ];
    let response = new apiResponse(addressData);

    response.loaded = true;

    this.addresses.value = response;

    return response
  }

  /*
  * Set address and call callback
  * Addresses with sid starting with "_" are considered new and not yet saved to the server
  * Addresses with other sids are considered saved addresses and will not be overwritten. Instead, a new address is created.
  * 
  */
  setAddr(type, addr, cmd) {
    if (!addr) return
    if (cmd == "update" && addr.sid && !String(addr.sid).startsWith("_")) {
        addr.sid = generateUUID("_");
        this.addresses.value.rawData.push(addr);
    }
    if (isFunction(this.onAddrChange)) this.onAddrChange(type, addr);
    if (type == "R") this.raddr = addr;
    else if (type == "L") this.laddr = addr;

    return addr
  }

  /*
  * Add new address
  */
  addAddr(type) { 
    let newAddr = this.addrTemplate();
    this.addresses.value.rawData.push(newAddr);
    this.setAddr(type, newAddr);
    return newAddr
  }

  addrTemplate() {
    return { sid: generateUUID("_"), firstname: undefined}
  }


}

class BankaccountManager {
  constructor() {
    const sessionStore = useSessionStore();

    this.authStore = useAuthStore();
    this.bankaccount = {};
    this.onBankaccountChange = null;

    const { checkout, bankaccounts } = storeToRefs(sessionStore);

    this.checkout = checkout;
    this.bankaccounts = bankaccounts;

    // Persisted store data loses class methods after reload; restore apiResponse wrapper.
    if (bankaccounts.value?.rawData) {
      bankaccounts.value = new apiResponse(bankaccounts.value.rawData);
    } else {
      this.createBankaccounts();
    }
  }

  async refreshBankaccounts() {
    if (!this.authStore.user) {
      let newBankaccounts = this.filterLocalBankaccounts(this.bankaccounts.value.data());
      if (newBankaccounts.length === 0) {
        newBankaccounts = this.createBankaccounts().data();
      }
      this.bankaccounts.value = new apiResponse(newBankaccounts);
      this.bankaccounts.value.loaded = true;
      return Promise.resolve(this.bankaccounts.value);
    }

    const kid = this.authStore.user?.kid;
    const loadRequest = kid
      ? ppapiClient.get(`users/${kid}/bankaccounts`)
      : Promise.resolve(new apiResponse([]));

    return loadRequest
      .then((response) => {
        const serverData = response?.data ? response.data() : [];
        const serverBankaccounts = Array.isArray(serverData)
          ? serverData.map((entry) => this.normalizeBankaccount(entry)).filter(Boolean)
          : [];

        if (Array.isArray(serverBankaccounts) && serverBankaccounts.length > 0) {
          const localData = this.bankaccounts.value?.data ? this.bankaccounts.value.data() : [];
          const localBankaccounts = Array.isArray(localData) ? this.filterLocalBankaccounts(localData) : [];
          this.bankaccounts.value = new apiResponse([...localBankaccounts, ...serverBankaccounts]);
        } else {
          const localData = this.bankaccounts.value?.data ? this.bankaccounts.value.data() : [];
          const localBankaccounts = Array.isArray(localData) ? this.filterLocalBankaccounts(localData) : [];
          this.createBankaccounts(localBankaccounts);
        }

        this.bankaccounts.value.loaded = true;
      })
      .catch((err) => {
        logger.warn('[bankaccount-manager] Error loading bankaccounts from db', err);
        // Keep checkout functional even if endpoint is unavailable.
        const localData = this.bankaccounts.value?.data ? this.bankaccounts.value.data() : [];
        const localBankaccounts = Array.isArray(localData) ? this.filterLocalBankaccounts(localData) : [];
        this.bankaccounts.value = new apiResponse(localBankaccounts.length > 0 ? localBankaccounts : [this.bankaccountTemplate()]);
        this.bankaccounts.value.loaded = true;
      });
  }

  filterLocalBankaccounts(bankaccounts) {
    if (!Array.isArray(bankaccounts)) return [];

    return bankaccounts.filter((entry) =>
      entry
      && String(entry.zid || '').startsWith('_')
      && (entry.owner || entry.iban)
    );
  }

  normalizeBankaccount(bankaccount) {
    if (!bankaccount || typeof bankaccount !== 'object') return null;

    const ownerValue = typeof bankaccount.owner === 'string' ? bankaccount.owner : '';
    const holderValue = typeof bankaccount.holderName === 'string' ? bankaccount.holderName : '';
    const ownerName = ownerValue || holderValue || '';
    const holderName = ownerName || holderValue || ownerValue || '';

    return {
      ...bankaccount,
      zid: typeof bankaccount.zid !== 'undefined' ? bankaccount.zid : generateUUID('_'),
      holderName,
      owner: ownerName,
      iban: typeof bankaccount.iban === 'string' ? bankaccount.iban : '',
      bankName: typeof bankaccount.bankName === 'string' ? bankaccount.bankName : '',
      bic: typeof bankaccount.bic === 'string' ? bankaccount.bic : '',
      accountNumber: typeof bankaccount.accountNumber === 'string' ? bankaccount.accountNumber : '',
      blz: typeof bankaccount.blz === 'string' ? bankaccount.blz : ''
    };
  }

  async init() {
    const selectedZid = this.checkout.value?.bankaccount || null;

    await this.refreshBankaccounts();

    const available = this.bankaccounts.value?.data ? this.bankaccounts.value.data() : [];
    if (!Array.isArray(available) || available.length === 0) {
      this.createBankaccounts();
    }

    const refreshedAvailable = this.bankaccounts.value.data();
    const selected = selectedZid
      ? refreshedAvailable.find((entry) => entry?.zid == selectedZid)
      : null;

    if (selected) {
      this.setBankaccount(selected);
    } else {
      this.setBankaccount(refreshedAvailable[0]);
    }
  }

  createBankaccounts(bankaccountData) {
    const fallback = Array.isArray(bankaccountData) && bankaccountData.length > 0
      ? bankaccountData
      : [this.bankaccountTemplate()];

    const response = new apiResponse(fallback);
    response.loaded = true;
    this.bankaccounts.value = response;

    return response;
  }

  setBankaccount(bankaccount, cmd) {
    if (!bankaccount) return null;

    const normalized = this.normalizeBankaccount(bankaccount);
    if (!normalized) return null;

    if (!normalized.zid) {
      normalized.zid = generateUUID('_');
    }

    if (cmd === 'update' && normalized.zid && !String(normalized.zid).startsWith('_')) {
      normalized.zid = generateUUID('_');
      this.bankaccounts.value.rawData.push(normalized);
    }

    const list = Array.isArray(this.bankaccounts.value.rawData) ? this.bankaccounts.value.rawData : [];
    const existing = list.find((entry) => entry && entry.zid == normalized.zid);
    if (existing) {
      list.splice(list.indexOf(existing), 1, normalized);
    } else {
      list.push(normalized);
    }

    this.bankaccounts.value.rawData = list;
    this.bankaccount = normalized;

    if (this.checkout.value && typeof this.checkout.value === 'object') {
      this.checkout.value.bankaccount = normalized.zid;
    }

    if (isFunction(this.onBankaccountChange)) {
      this.onBankaccountChange(normalized);
    }

    return normalized;
  }

  addBankaccount() {
    const newBankaccount = this.bankaccountTemplate();
    this.bankaccounts.value.rawData.push(newBankaccount);
    this.setBankaccount(newBankaccount);
    return newBankaccount;
  }

  bankaccountTemplate() {
    return {
      zid: generateUUID('_'),
      owner: '',
      iban: ''
    };
  }
}

export { AddressManager as A, BankaccountManager as B, Order as O };
